import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  fetchDealsPaginated,
  fetchDealsStats,
} from "../requests/Uploaded_Deals/UploadedDealsService";

const useDeals = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 15,
    hasNext: false,
    hasPrev: false,
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    dateFrom: "",
    dateTo: "",
    sortBy: "created_at",
    sortOrder: "desc",
  });

  const searchTimeoutRef = useRef(null);

  const debouncedSearch = useCallback((searchTerm) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchTerm }));
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
    }, 500);
  }, []);

  const computePaginationFromResponse = useCallback((p, limit) => {
    const totalItems = p?.total ?? p?.totalItems ?? 0;
    const totalPages = Math.max(1, (p?.last_page ?? p?.totalPages ?? Math.ceil(totalItems / limit)) || 1);
    const currentPage = Math.max(1, Math.min(p?.current_page ?? p?.currentPage ?? 1, totalPages));
    return {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: limit,
      hasNext: currentPage < totalPages,
      hasPrev: currentPage > 1,
    };
  }, []);

  const fetchDeals = useCallback(
    async (
      page = 1,
      limit = 15,
      search = "",
      dateFrom = "",
      dateTo = "",
      status = "all",
      sortOrder = "desc",
      sortBy = "created_at"
    ) => {
      setLoading(true);
      setError(null);

      const statusParam = status === "all" ? "" : status;

      try {
        const response = await fetchDealsPaginated(
          page,
          limit,
          search,
          dateFrom,
          dateTo,
          statusParam,
          sortOrder,
          sortBy
        );

        if (!response.status) {
          setError(response.message || "Error fetching deals");
          toast.error(response.message || "Error fetching deals");
          setDeals([]);
          return;
        }

        const list = Array.isArray(response.data) ? response.data : [];
        const p = response.pagination || {};
        const computed = computePaginationFromResponse(p, limit);

        setDeals(list);
        setPagination({
          ...computed,
          totalPages: p?.last_page ?? computed.totalPages,
          hasNext: computed.currentPage < computed.totalPages,
          hasPrev: computed.currentPage > 1,
        });
      } catch (err) {
        setError("Network error occurred");
        toast.error("Network error occurred");
        console.error("Error fetching deals:", err);
        setDeals([]);
      } finally {
        setLoading(false);
      }
    },
    [computePaginationFromResponse]
  );

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetchDealsStats();
      if (res.status && res.data) {
        const d = res.data;
        setStats({
          total: d.count_deal ?? 0,
          pending: d.count_pending_deal ?? 0,
          approved: d.count_approved_deal ?? 0,
          rejected: d.count_rejected_deal ?? 0,
        });
      }
    } catch (e) {
      console.error("Error fetching deal stats:", e);
    }
  }, []);

  useEffect(() => {
    fetchDeals(
      pagination.currentPage,
      pagination.itemsPerPage,
      filters.search,
      filters.dateFrom,
      filters.dateTo,
      filters.status,
      filters.sortOrder,
      filters.sortBy
    );
  }, [
    pagination.currentPage,
    pagination.itemsPerPage,
    filters.search,
    filters.dateFrom,
    filters.dateTo,
    filters.status,
    filters.sortOrder,
    filters.sortBy,
    fetchDeals,
  ]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const changePage = useCallback((newPage) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  const changeItemsPerPage = useCallback((newLimit) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
      itemsPerPage: newLimit,
    }));
  }, []);

  const updateStatusFilter = useCallback((status) => {
    setFilters((prev) => ({ ...prev, status }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const updateDateFilter = useCallback((dateFrom, dateTo) => {
    setFilters((prev) => ({ ...prev, dateFrom, dateTo }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const updateSort = useCallback((sortBy, sortOrder) => {
    setFilters((prev) => ({ ...prev, sortBy: sortBy || prev.sortBy, sortOrder }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const clearDateFilter = useCallback(() => {
    setFilters((prev) => ({ ...prev, dateFrom: "", dateTo: "" }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({
      search: "",
      status: "all",
      dateFrom: "",
      dateTo: "",
      sortBy: "created_at",
      sortOrder: "desc",
    });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const refreshData = useCallback(() => {
    fetchDeals(
      pagination.currentPage,
      pagination.itemsPerPage,
      filters.search,
      filters.dateFrom,
      filters.dateTo,
      filters.status,
      filters.sortOrder,
      filters.sortBy
    );
    fetchStats();
  }, [
    fetchDeals,
    fetchStats,
    pagination.currentPage,
    pagination.itemsPerPage,
    filters.search,
    filters.dateFrom,
    filters.dateTo,
    filters.status,
    filters.sortOrder,
    filters.sortBy,
  ]);

  return {
    deals,
    loading,
    error,
    stats,
    pagination,
    filters,
    debouncedSearch,
    changePage,
    changeItemsPerPage,
    updateStatusFilter,
    updateDateFilter,
    updateSort,
    clearDateFilter,
    clearAllFilters,
    refreshData,
  };
};

export default useDeals;
